/**
 * recentReviewRecovery.js — sticky pointer to the most-recently-
 * uploaded lesson_review so the user can never get dumped back to
 * an empty upload zone after their upload reaches 100%.
 *
 * Bug it solves (production, Feb 8, 2026): user reaches 100%, the
 * upload finalisation does ~2.7s of `_wait` holds for the
 * Finalising → Complete animation, and during that window iOS
 * Safari backgrounds the page or React re-mounts on a visibility
 * change. The in-memory `active` state is lost and the user is
 * thrown back to the upload zone — even though the DB row exists
 * and is being processed.
 *
 * Strategy:
 *   • persist {reviewId, ts, status, fileName, fileSize, role,
 *     reviewType} to localStorage the moment the server confirms
 *     the row was created — keyed by user_id + role + reviewType
 *     so duplicate-pick recovery is correctly scoped.
 *   • on mount, read the pointer back; if the timestamp is fresh
 *     (<30 min), refetch the review and rehydrate `active`.
 *   • clear it when the review reaches a terminal state, OR when
 *     the user explicitly dismisses.
 *
 * Operator scoping rule (Feb 8, 2026, locked):
 *   • Same user + same role + recent upload → offer recovery
 *   • Different user OR different role OR different review_type
 *     → completely independent upload
 *   • Same user intentionally re-uploading later → allow explicit
 *     fresh upload (Dismiss action on the banner)
 *
 * Pure browser-side. No window/localStorage reads at module load —
 * every helper guards via `typeof window` so the module is import-
 * safe in tests.
 */

const KEY_PREFIX = "lr_recent_review:";          // namespaced per user
const FRESH_WINDOW_MS = 30 * 60 * 1000;          // 30 min

// Terminal statuses — once a review hits one of these we stop
// recovering it. Awaiting_payment is intentionally NON-terminal —
// the user still needs to be reminded to pay.
const TERMINAL_STATUSES = new Set([
  "complete", "completed", "failed", "cancelled",
]);


function _scopeKey(userId, role, reviewType) {
  // Owner-locked scoping (Feb 8, 2026) — pointer is keyed by user
  // AND role AND review_type so two ADIs uploading the same MOV
  // file don't collide, an ADI vs PDI on the same login don't
  // collide, and a Teaching-reflection vs Pupil-paid review on
  // the same file are tracked independently.
  const r = (role || "default").toString().toLowerCase().slice(0, 32);
  const t = (reviewType || "default").toString().toLowerCase().slice(0, 32);
  return `${KEY_PREFIX}${userId}:${r}:${t}`;
}


// Backwards-compat key for pointers written under the pre-scoping
// schema (Feb 8 morning). On read we fall back to the legacy key
// if the scoped key is missing — keeps in-flight uploads from a
// pre-redeploy session recoverable. Cleaned up automatically.
function _legacyKey(userId) {
  return `${KEY_PREFIX}${userId}`;
}

function _ls() {
  if (typeof window === "undefined") return null;
  try { return window.localStorage; } catch { return null; }
}


export function rememberRecentReview({
  userId, reviewId, status,
  fileName = null, fileSize = null,
  role = null, reviewType = null,
}) {
  if (!userId || !reviewId) return;
  const ls = _ls();
  if (!ls) return;
  try {
    const payload = JSON.stringify({
      reviewId,
      status: status || null,
      fileName: fileName || null,
      fileSize: typeof fileSize === "number" ? fileSize : null,
      role: role ? String(role).toLowerCase() : null,
      reviewType: reviewType ? String(reviewType).toLowerCase() : null,
      ts: Date.now(),
    });
    ls.setItem(_scopeKey(userId, role, reviewType), payload);
    // Also write the legacy key so cross-page surfaces (e.g. the
    // older callers that haven't yet adopted the
    // scoped lookup keep working. Negligible storage cost; cleared
    // when the scoped pointer is cleared.
    ls.setItem(_legacyKey(userId), payload);
  } catch { /* quota exceeded — never throw */ }
}


export function loadRecentReview(userId, role = null, reviewType = null) {
  if (!userId) return null;
  const ls = _ls();
  if (!ls) return null;
  // Try the scoped key first; fall back to legacy.
  const tries = role || reviewType
    ? [_scopeKey(userId, role, reviewType), _legacyKey(userId)]
    : [_legacyKey(userId)];
  for (const key of tries) {
    try {
      const raw = ls.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.reviewId || !parsed.ts) continue;
      if (Date.now() - parsed.ts > FRESH_WINDOW_MS) continue;
      return parsed;
    } catch { continue; }
  }
  return null;
}


export function clearRecentReview(userId, role = null, reviewType = null) {
  if (!userId) return;
  const ls = _ls();
  if (!ls) return;
  try {
    ls.removeItem(_scopeKey(userId, role, reviewType));
    ls.removeItem(_legacyKey(userId));
  } catch { /* never throw */ }
}


export function isTerminalStatus(status) {
  if (!status) return false;
  return TERMINAL_STATUSES.has(String(status).toLowerCase());
}


/**
 * Duplicate-pick detector — operator-scoped (Feb 8, 2026).
 *
 * Returns the recovery pointer when:
 *   • a sticky recovery pointer exists for this user + role +
 *     review_type combination, AND
 *   • the pointer's `fileName` and `fileSize` match the just-picked
 *     file (within ±1 byte tolerance — File.size is exact on iOS),
 *     AND
 *   • the pointer's status is non-terminal.
 *
 * Returns null otherwise. Used by `onFiles` to surface the recovery
 * banner BEFORE starting a new upload, so re-picking the same video
 * for the SAME role/review_type doesn't kick off a duplicate
 * session. Different role or review_type → independent upload (no
 * collision).
 */
export function findRecoveryByFile(userId, file, role = null, reviewType = null) {
  if (!userId || !file) return null;
  const pointer = loadRecentReview(userId, role, reviewType);
  if (!pointer) return null;
  if (isTerminalStatus(pointer.status)) return null;
  if (!pointer.fileName || typeof pointer.fileSize !== "number") return null;
  if (pointer.fileName !== file.name) return null;
  if (Math.abs(pointer.fileSize - file.size) > 1) return null;
  // If the pointer carries scope data, double-check it matches.
  // (Defensive — `loadRecentReview` already filtered by scope key.)
  if (role && pointer.role && pointer.role !== String(role).toLowerCase()) return null;
  if (reviewType && pointer.reviewType
      && pointer.reviewType !== String(reviewType).toLowerCase()) return null;
  return pointer;
}


/**
 * Helper for the LessonReview mount-time recovery effect. Given the
 * latest history list (already fetched via /api/lesson-review/list),
 * picks the freshest still-processing review.
 *
 * Operator scoping rule (May 14, 2026 — production bug fix on iPhone
 * Chrome: stale "video/upload in progress" banner surfaced for
 * non-flight statuses like `awaiting_payment` and for rows that hadn't
 * been touched in over 10 minutes).
 *
 * A row is considered "actively in flight" ONLY when:
 *   1. its status is one of the pipeline-active states tracked by the
 *      backend `/api/lesson-review/interrupted` endpoint — these are
 *      the ONLY statuses where the server is genuinely working on
 *      the review: queued | transcribing | grading | extracting_clips
 *      | preparing_release.
 *      Statuses explicitly excluded: complete | failed | cancelled
 *      (terminal), awaiting_payment (paused on user, not server),
 *      uploading | chunked_* (handled by chunked-upload sweepers).
 *   2. created_at within the last 15 minutes (was already the case).
 *   3. updated_at within the last 10 minutes — guarantees the row is
 *      still being actively touched. Operational Continuity Phase A
 *      sweeper auto-fails rows untouched for 60 min; we use the
 *      tighter 10 min threshold here so a row whose sweeper run is
 *      pending doesn't briefly resurrect a ghost banner.
 *
 * Returns the matching item or null.
 */
const PIPELINE_ACTIVE_STATUSES = new Set([
  "queued", "transcribing", "grading",
  "extracting_clips", "preparing_release",
]);

export function findRecentInFlightReview(historyItems) {
  if (!Array.isArray(historyItems) || historyItems.length === 0) return null;
  const createdCutoff = Date.now() - 15 * 60 * 1000;
  const updatedCutoff = Date.now() - 10 * 60 * 1000;
  for (const item of historyItems) {
    if (!item || !item.id) continue;
    const status = String(item.status || "").toLowerCase();
    if (!PIPELINE_ACTIVE_STATUSES.has(status)) continue;
    const createdAt = item.created_at ? Date.parse(item.created_at) : 0;
    if (!createdAt || isNaN(createdAt)) continue;
    if (createdAt < createdCutoff) continue;
    // updated_at is the strongest signal of "still being touched".
    // Fall back to created_at when missing — gives newly-created
    // rows that haven't yet been updated a fair chance to surface.
    const updatedAt = item.updated_at
      ? Date.parse(item.updated_at) : createdAt;
    if (!updatedAt || isNaN(updatedAt)) continue;
    if (updatedAt < updatedCutoff) continue;
    return item;
  }
  return null;
}
